import { init, captureException, setUser, browserTracingIntegration, globalHandlersIntegration, captureConsoleIntegration } from '@sentry/browser';

const system = SYSTEM;
const ENV_NAME = IS_ONLINE ? 'pro' : IS_PRODUCTION ? 'dev' : 'local';


const tracesSampleRateMap = {
    CN: 0.5,
    TW: 1,
    JP: 0.5,
    EN: 1,
    EM: 1,
}

function getSentryConfig() {
    const SENTRY_CONFIG = {
        CN: {
            dsn: 'https://32ba5348016b4f5995ce5f952b3caf47@sentry.umu.cn/15',
        },
        TW: {
            dsn: 'https://92483313a7334bca8e98aa0260c1449a@sentry.umu.tw/16',
        },
        JP: {
            dsn: 'https://f8f5424567ff44a693b3f28fd1495481@sentry.umu.co/18',
        },
        EN: {
            dsn: 'https://8501b77f52624460b9f26f125381284e@sentry.umu.com/17',
        },
        EM: {
            dsn: 'https://2ee9d75299d841aeb6e44ccd0d6238ef@sentry.umu.com/18',
        },
    };
    const config = SENTRY_CONFIG[system.toUpperCase() as keyof typeof SENTRY_CONFIG];
    return {
        sentryDSN: config.dsn,
        tracesSampleRate: tracesSampleRateMap[system.toUpperCase() as keyof typeof tracesSampleRateMap],
        release: window.VERSION,
    };
}

function registerSentry() {
    if (ENV_NAME !== 'pro') {
        return;
    }
    const { sentryDSN, tracesSampleRate, release } = getSentryConfig();

    if (!sentryDSN) {
        return;
    }

    init({
        environment: ENV_NAME,
        dsn: sentryDSN,
        integrations: [
            browserTracingIntegration(),
            globalHandlersIntegration({
                onunhandledrejection: true,
                onerror: true,
            }),
            captureConsoleIntegration({ levels: ['error'] }),
        ],
        ignoreErrors: [
            '[video-takeover-extract]',
            'empty host rule',
            "Can't find variable: WebViewJavascriptBridge",
            'WebViewJavascriptBridge is not defined',
            'AbortError: The operation was aborted.',
            'Load failed',
            'TypeError: Load failed',
            'Failed to fetch',
            'TypeError: Failed to fetch',
            'No error message',
            'The network connection was lost.',
            "Can't find variable: cordova",
            'appcb is not defined',
            '網路連線中斷。',
            '已取消',
            'Internet連線已斷開。',
            'LanxinJsBridge is not defined',
            "Can't find variable: LanxinJsBridge",
            "Can't find variable: ResizeObserver",
            'window.android.unLoad is not a function',
            /^AbortError: The play/i,
            /^NotAllowedError: The request is not/i,
            /^The fetching process for the media resource was aborted/i,
        ],
        denyUrls: [/^chrome:/i, /chrome-extension:/i, /^file:/i],
        // Set tracesSampleRate to 1.0 to capture 100% of transactions for performance monitoring.
        // We recommend adjusting this value in production
        release,
        maxBreadcrumbs: 50,
        tracesSampleRate: tracesSampleRate,
        // https://docs.sentry.io/platforms/javascript/configuration/filtering/
        beforeSend: (event: any, _hint: any) => {
            // filter out UnhandledRejection errors that have no information https://github.com/getsentry/sentry-javascript/issues/3440
            const ignoreErrorList = ['Please use the NodeViewWrapper component for your node view.', "'get' on proxy: property 'javaEnabled' is a read-only", 'Loading chunk 1 failed.', "Can't find variable: Aplus4Native"];
            if (event !== undefined && event.exception !== undefined && event.exception.values !== undefined && event.exception.values.length === 1) {
                const e = event.exception.values[0] || {};
                const exceptionMessage = e.value;
                if (e.type === 'UnhandledRejection' && exceptionMessage && exceptionMessage.indexOf('Non-Error promise rejection captured with value:') > -1) {
                    return null;
                }
                // 忽略 [object Event] 错误
                if (e.type === 'Event') {
                    return null;
                }
                // 忽略一些错误
                if (ignoreErrorList.some((error) => exceptionMessage && exceptionMessage.indexOf(error) > -1)) {
                    return null;
                }
            }
            return event;
        },
    });
    window.Sentry = { init, captureException, setUser };
    const userInfo = (window.pageData.data && (window.pageData.data.teacher_info || window.pageData.data.user_info)) || {};
    const params: any = {};
    if (userInfo.enterpriseId) {
        params.eId = userInfo.enterpriseId;
    }
    if (userInfo.umu_id) {
        params.uId = userInfo.umu_id;
    }
    if (userInfo.userName) {
        params.username = userInfo.userName;
    }
    if (userInfo.email) {
        params.email = userInfo.email;
    }
    setUser(params);
}

export default registerSentry;
